import { supabase } from '../../core/config/supabase';
import { TeamEntity } from '../entities/TeamEntity';
import { TeamMemberEntity } from '../entities/TeamMemberEntity';
import { TeamInvitationEntity } from '../entities/TeamInvitationEntity';

export class TeamDataSource {
  // ── Team CRUD ─────────────────────────────────────────────

  async create(team: Omit<TeamEntity, 'id' | 'created_at' | 'updated_at'>): Promise<TeamEntity> {
    const { data, error } = await supabase
      .from('teams')
      .insert(team)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as TeamEntity;
  }

  async getById(id: string): Promise<TeamEntity | null> {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }

    return data as TeamEntity;
  }

  async getUserTeams(userId: string): Promise<TeamEntity[]> {
    const { data, error } = await supabase.rpc('get_user_teams', {
      requesting_user_id: userId,
    });

    if (error) throw new Error(error.message);

    return (data as TeamEntity[]) || [];
  }

  async getPublicTeams(limit: number, offset: number): Promise<TeamEntity[]> {
    const { data, error } = await supabase.rpc('get_public_teams', {
      page_limit: limit,
      page_offset: offset,
    });

    if (error) throw new Error(error.message);

    return (data as TeamEntity[]) || [];
  }

  async update(id: string, team: Partial<TeamEntity>): Promise<TeamEntity> {
    const { data, error } = await supabase
      .from('teams')
      .update({ ...team, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as TeamEntity;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('teams')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  async countOwnedTeams(userId: string): Promise<number> {
    const { data, error } = await supabase.rpc('count_active_teams', {
      p_user_id: userId,
    });

    if (error) throw new Error(error.message);

    return data ?? 0;
  }

  // ── Members ───────────────────────────────────────────────

  async addMember(teamId: string, userId: string): Promise<TeamMemberEntity> {
    // Insert the member row
    const { error: insertError } = await supabase
      .from('team_members')
      .insert({ team_id: teamId, user_id: userId });

    if (insertError) throw new Error(insertError.message);

    // Fetch with profile join via RPC
    const members = await this.getMembers(teamId);
    const member = members.find((m) => m.user_id === userId);
    if (!member) throw new Error('Member not found after insert');

    return member;
  }

  async getMembers(teamId: string): Promise<TeamMemberEntity[]> {
    const { data, error } = await supabase.rpc('get_team_members', {
      target_team_id: teamId,
    });

    if (error) throw new Error(error.message);

    return (data as TeamMemberEntity[]) || [];
  }

  async removeMember(teamId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', userId);

    if (error) throw new Error(error.message);
  }

  // ── Invitations ───────────────────────────────────────────

  async sendInvitation(
    teamId: string,
    inviterId: string,
    inviteeId: string,
    message?: string,
  ): Promise<TeamInvitationEntity> {
    const { data, error } = await supabase
      .from('team_invitations')
      .insert({
        team_id: teamId,
        inviter_id: inviterId,
        invitee_id: inviteeId,
        message: message || null,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    // Fetch with team name via RPC to get full details
    const received = await this.getInvitationsReceived(inviteeId);
    const invitation = received.find((i) => i.id === (data as { id: string }).id);
    if (!invitation) throw new Error('Invitation not found after insert');

    return invitation;
  }

  async getInvitationsReceived(userId: string): Promise<TeamInvitationEntity[]> {
    const { data, error } = await supabase.rpc('get_team_invitations_received', {
      requesting_user_id: userId,
    });

    if (error) throw new Error(error.message);

    return (data as TeamInvitationEntity[]) || [];
  }

  async getInvitationsSent(userId: string): Promise<TeamInvitationEntity[]> {
    const { data, error } = await supabase.rpc('get_team_invitations_sent', {
      requesting_user_id: userId,
    });

    if (error) throw new Error(error.message);

    return (data as TeamInvitationEntity[]) || [];
  }

  async updateInvitationStatus(invitationId: string, status: string): Promise<void> {
    const { error } = await supabase
      .from('team_invitations')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', invitationId);

    if (error) throw new Error(error.message);
  }
}
